import { useCallback, useState } from 'react';

/**
 * Lifts a Pattern-2 fullscreen form's submitting/dirty state into the host
 * that owns the `FullscreenFormDialog` (see `docs/FULLSCREEN_FORM_MODALS.md`).
 *
 * A "pure form + parent dialog" component reports its state upward via
 * `onSubmittingChange` / `onDirtyChange`; the host feeds that back into the
 * dialog's `isSubmitting` (disables the footer, swaps to "Saving…") and
 * `skipCloseConfirmation={!isDirty}` (a pristine form closes without the
 * "Discard changes?" prompt). This hook is the small piece of glue every
 * such host otherwise re-implements as two `useState`s plus a reset.
 *
 * Usage:
 * ```tsx
 * const formState = useLiftedFormDialogState();
 * // ...on close: formState.reset();
 * <FullscreenFormDialog
 *   isSubmitting={formState.isSubmitting}
 *   skipCloseConfirmation={!formState.isDirty}
 *   ...
 * >
 *   <SomeForm
 *     onSubmittingChange={formState.onSubmittingChange}
 *     onDirtyChange={formState.onDirtyChange}
 *   />
 * </FullscreenFormDialog>
 * ```
 *
 * Call `reset()` when the dialog closes if the host outlives the dialog (the
 * flags are held in the host, so a stale `true` would otherwise leak into the
 * next open). Hosts whose dialog component fully unmounts on close don't need
 * it, but calling it is harmless.
 */
export function useLiftedFormDialogState() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const reset = useCallback(() => {
    setIsSubmitting(false);
    setIsDirty(false);
  }, []);

  return {
    isSubmitting,
    isDirty,
    onSubmittingChange: setIsSubmitting,
    onDirtyChange: setIsDirty,
    reset,
  };
}
